import React, { useState } from 'react';

type ActionType = 'create' | 'update' | 'delete' | 'pause' | 'resume' | 'api_call' | 'export';

interface ActionLog {
  id: string;
  timestamp: string;
  user: string;
  userEmail: string;
  actionType: ActionType;
  resource: string;
  resourceId: string;
  description: string;
  ipAddress: string;
  userAgent: string;
  changes?: {
    field: string;
    oldValue: string;
    newValue: string;
  }[];
  canUndo: boolean;
}

export const ActionLogPage: React.FC = () => {
  const [actionFilter, setActionFilter] = useState<ActionType | 'all'>('all');
  const [userFilter, setUserFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLog, setSelectedLog] = useState<ActionLog | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Mock data
  const actionLogs: ActionLog[] = [
    {
      id: '1',
      timestamp: '2026-01-27 02:15 PM',
      user: 'John Doe',
      userEmail: 'john@goodpower.com',
      actionType: 'pause',
      resource: 'Campaign',
      resourceId: 'camp-001',
      description: 'Paused campaign "Summer Sale 2026"',
      ipAddress: '192.168.1.45',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      canUndo: true,
    },
    {
      id: '2',
      timestamp: '2026-01-27 01:30 PM',
      user: 'Jane Smith',
      userEmail: 'jane@goodpower.com',
      actionType: 'update',
      resource: 'Campaign',
      resourceId: 'camp-002',
      description: 'Updated budget for "Spring Collection"',
      ipAddress: '192.168.1.78',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      changes: [
        { field: 'daily_budget', oldValue: '$500', newValue: '$750' },
        { field: 'status', oldValue: 'active', newValue: 'active' },
      ],
      canUndo: true,
    },
    {
      id: '3',
      timestamp: '2026-01-27 12:45 PM',
      user: 'API Client',
      userEmail: 'api@external.com',
      actionType: 'api_call',
      resource: 'Analytics',
      resourceId: 'api-req-123',
      description: 'Fetched campaign analytics via API',
      ipAddress: '54.23.156.89',
      userAgent: 'axios/1.6.0',
      canUndo: false,
    },
    {
      id: '4',
      timestamp: '2026-01-27 11:20 AM',
      user: 'Marketing Team',
      userEmail: 'marketing@goodpower.com',
      actionType: 'create',
      resource: 'Campaign',
      resourceId: 'camp-005',
      description: 'Created new campaign "Winter Promo 2026"',
      ipAddress: '192.168.1.92',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      changes: [
        { field: 'name', oldValue: '', newValue: 'Winter Promo 2026' },
        { field: 'budget', oldValue: '', newValue: '$2,000' },
        { field: 'platform', oldValue: '', newValue: 'Meta' },
      ],
      canUndo: true,
    },
    {
      id: '5',
      timestamp: '2026-01-27 10:00 AM',
      user: 'John Doe',
      userEmail: 'john@goodpower.com',
      actionType: 'export',
      resource: 'Report',
      resourceId: 'report-456',
      description: 'Exported Q1 2026 performance report to CSV',
      ipAddress: '192.168.1.45',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
      canUndo: false,
    },
    {
      id: '6',
      timestamp: '2026-01-26 04:30 PM',
      user: 'Admin',
      userEmail: 'admin@goodpower.com',
      actionType: 'delete',
      resource: 'Campaign',
      resourceId: 'camp-003',
      description: 'Deleted campaign "Old Test Campaign"',
      ipAddress: '192.168.1.10',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
      canUndo: false,
    },
  ];

  const users = Array.from(new Set(actionLogs.map(log => log.user)));

  const filteredLogs = actionLogs.filter(log => {
    if (actionFilter !== 'all' && log.actionType !== actionFilter) return false;
    if (userFilter !== 'all' && log.user !== userFilter) return false;
    if (searchQuery && !log.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const handleViewDetails = (log: ActionLog) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const handleUndoAction = (log: ActionLog) => {
    if (!log.canUndo) return;
    if (confirm(`Are you sure you want to undo this action?\n\n${log.description}`)) {
      console.log('Undoing action:', log.id);
      // TODO: Call API
    }
  };

  const handleExportLogs = () => {
    console.log('Exporting logs to CSV');
    // TODO: Implement CSV export
  };

  const getActionIcon = (type: ActionType) => {
    switch (type) {
      case 'create':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />;
      case 'update':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />;
      case 'delete':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />;
      case 'pause':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'resume':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'api_call':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />;
      case 'export':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />;
    }
  };

  const getActionColor = (type: ActionType) => {
    switch (type) {
      case 'create': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'update': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      case 'delete': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'pause': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'resume': return 'text-green-600 bg-green-50 dark:bg-green-900/20';
      case 'api_call': return 'text-purple-600 bg-purple-50 dark:bg-purple-900/20';
      case 'export': return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Action Log</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            System-wide activity audit trail
          </p>
        </div>
        <button
          onClick={handleExportLogs}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Export to CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Action Type
            </label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as ActionType | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="pause">Pause</option>
              <option value="resume">Resume</option>
              <option value="api_call">API Call</option>
              <option value="export">Export</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              User
            </label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Users</option>
              {users.map((user) => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search actions..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Actions</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{actionLogs.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Today</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {actionLogs.filter(log => log.timestamp.includes('2026-01-27')).length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Active Users</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{users.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Can Undo</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {actionLogs.filter(log => log.canUndo).length}
          </p>
        </div>
      </div>

      {/* Action Logs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                    {log.timestamp}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{log.user}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{log.userEmail}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.actionType)}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {getActionIcon(log.actionType)}
                      </svg>
                      <span className="capitalize">{log.actionType.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm text-gray-900 dark:text-white">{log.description}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {log.resource} · {log.resourceId}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleViewDetails(log)}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        Details
                      </button>
                      {log.canUndo && (
                        <button
                          onClick={() => handleUndoAction(log)}
                          className="text-sm text-yellow-600 hover:text-yellow-700 dark:text-yellow-400"
                        >
                          Undo
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Action Details</h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedLog.description}</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Timestamp</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedLog.timestamp}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">User</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedLog.user}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{selectedLog.userEmail}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Action Type</p>
                  <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium ${getActionColor(selectedLog.actionType)}`}>
                    <span className="capitalize">{selectedLog.actionType.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Resource</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedLog.resource}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{selectedLog.resourceId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">IP Address</p>
                  <code className="text-sm font-mono text-gray-900 dark:text-white">{selectedLog.ipAddress}</code>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Can Undo</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedLog.canUndo ? 'Yes' : 'No'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">User Agent</p>
                <code className="block px-3 py-2 bg-gray-100 dark:bg-gray-900 rounded text-xs text-gray-900 dark:text-white break-all">
                  {selectedLog.userAgent}
                </code>
              </div>

              {selectedLog.changes && selectedLog.changes.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">Changes Made:</p>
                  <div className="space-y-2">
                    {selectedLog.changes.map((change, idx) => (
                      <div key={idx} className="bg-gray-50 dark:bg-gray-900 p-3 rounded">
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{change.field}</p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400 rounded">
                            {change.oldValue || '(empty)'}
                          </span>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400 rounded">
                            {change.newValue}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              {selectedLog.canUndo && (
                <button
                  onClick={() => {
                    handleUndoAction(selectedLog);
                    setShowDetailsModal(false);
                  }}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  Undo This Action
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionLogPage;
